import type { PresignUploadInput, SubmitInspectionInput } from '@prooffleet/contracts';
import { ApiError, api, uploadToStorage } from '@prooffleet/client';
import {
  dequeue,
  deleteDraft,
  getBlob,
  markBlobUploaded,
  queued,
  updateQueued,
  type QueuedSubmission,
} from './db.js';

export interface SyncStatus {
  running: boolean;
  pending: number;
  lastError: string | null;
  lastSyncAt: string | null;
}

type Listener = (status: SyncStatus) => void;

let status: SyncStatus = { running: false, pending: 0, lastError: null, lastSyncAt: null };
const listeners = new Set<Listener>();

function emit(next: Partial<SyncStatus>): void {
  status = { ...status, ...next };
  for (const listener of listeners) listener(status);
}

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener);
  listener(status);
  return () => listeners.delete(listener);
}

export function syncStatus(): SyncStatus {
  return status;
}

/** Ошибка клиента неисправима повтором — очередь на ней не должна залипать. */
function isPermanent(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429;
}

async function uploadPending(submission: QueuedSubmission): Promise<Map<string, {
  objectKey: string;
  versionId: string | null;
  sha256: string;
  bytes: number;
}>> {
  const uploaded = new Map<
    string,
    { objectKey: string; versionId: string | null; sha256: string; bytes: number }
  >();

  // Голосовые заметки идут тем же путём, что снимки: локальный blob → presign
  // → прямая загрузка в хранилище. Разделять их незачем, различает только `kind`.
  const localIds = submission.payload.items.flatMap((item) => [
    ...item.photoLocalIds,
    ...(item.audioLocalId === null ? [] : [item.audioLocalId]),
  ]);

  for (const localId of localIds) {
    const blob = await getBlob(localId);
    if (blob === undefined) throw new Error(`Вложение ${localId} потеряно локально`);

    if (blob.objectKey !== null) {
      uploaded.set(localId, {
        objectKey: blob.objectKey,
        versionId: blob.versionId,
        sha256: blob.sha256,
        bytes: blob.bytes.byteLength,
      });
      continue;
    }

    // Подписанная ссылка протухает: при офлайн-осмотре снимок лежит часами,
    // поэтому URL запрашивается заново в момент отправки, а не хранится.
    const presignInput: PresignUploadInput = {
      unitId: submission.payload.unitId,
      sha256: blob.sha256,
      kind: blob.mime.startsWith('audio/') ? 'audio' : 'photo',
      extension: blob.extension,
      bytes: blob.bytes.byteLength,
    };

    const presigned = await api<{ url: string; objectKey: string }>('/uploads/presign', {
      method: 'POST',
      body: presignInput,
    });

    const { versionId } = await uploadToStorage(
      presigned.url,
      new Uint8Array(blob.bytes),
      blob.mime,
    );

    await markBlobUploaded(localId, presigned.objectKey, versionId);
    uploaded.set(localId, {
      objectKey: presigned.objectKey,
      versionId,
      sha256: blob.sha256,
      bytes: blob.bytes.byteLength,
    });
  }

  return uploaded;
}

async function submitOne(submission: QueuedSubmission): Promise<void> {
  const uploaded = await uploadPending(submission);

  const payload: SubmitInspectionInput = {
    ...submission.payload,
    items: submission.payload.items.map(({ photoLocalIds, audioLocalId, ...item }) => {
      const ref = (localId: string) => {
        const blob = uploaded.get(localId);
        if (blob === undefined) throw new Error(`Вложение ${localId} не загружено`);
        return {
          objectKey: blob.objectKey,
          versionId: blob.versionId,
          sha256: blob.sha256,
          bytes: blob.bytes,
        };
      };
      return {
        ...item,
        photos: photoLocalIds.map(ref),
        audio: audioLocalId === null ? null : ref(audioLocalId),
      };
    }),
  };

  await api('/inspections', { method: 'POST', body: payload });

  // Локальная копия чистится только после подтверждения сервером.
  await dequeue(submission.clientUuid);
  await deleteDraft(submission.draftId);
}

let inFlight: Promise<void> | null = null;

/** Прогон очереди. Параллельные вызовы схлопываются в один. */
export async function runSync(): Promise<void> {
  if (inFlight !== null) return inFlight;

  inFlight = (async () => {
    emit({ running: true, lastError: null });

    try {
      const pending = await queued();
      emit({ pending: pending.length });

      for (const submission of pending) {
        try {
          await submitOne(submission);
          emit({ pending: (await queued()).length });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Неизвестная ошибка';

          if (isPermanent(error)) {
            // Сервер отверг содержимое — повторять бессмысленно.
            // Осмотр остаётся в очереди с пометкой, чтобы не исчез молча.
            await updateQueued({
              ...submission,
              attempts: submission.attempts + 1,
              lastError: message,
            });
          } else {
            await updateQueued({
              ...submission,
              attempts: submission.attempts + 1,
              lastError: message,
            });
          }

          emit({ lastError: message });
        }
      }

      emit({ lastSyncAt: new Date().toISOString() });
    } finally {
      emit({ running: false, pending: (await queued()).length });
      inFlight = null;
    }
  })();

  return inFlight;
}

let started = false;

/** Запуск фоновой синхронизации: по возврату связи и по таймеру. */
export function startSync(intervalMs = 30_000): void {
  if (started) return;
  started = true;

  const trigger = (): void => {
    if (navigator.onLine) void runSync();
  };

  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trigger();
  });
  window.setInterval(trigger, intervalMs);

  trigger();
  void queued().then((items) => emit({ pending: items.length }));
}
